import Grid from '@mui/material/GridLegacy';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ReactMarkdown from 'react-markdown';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { GetFAQBySpecialtyFn } from '../../data/apiCalls';
import NoResultsOverlay from '../Errors/NoResultsOverlay';
import PageBreadcrumbs from '../shared/Pages/PageBreadcrumbs';
import { mapAndTransformSpecialtyString } from '../../scripts/faqPageHelpers';
import MarkdownLinkRenderer from '../shared/MarkdownLinkRenderer';
import MarkdownParagraphRenderer from '../shared/MarkdownParagraphRenderer';
import MarkdownUnorderListRenderer from '../shared/MarkdownUnorderedListRenderer';
import MarkdownListItemRenderer from '../shared/MarkdownListItemRenderer';
import { TOP_LEVEL_ROUTES } from '../../scripts/routes';
import FaqsPageSkeletonLoading from './FaqsPageSkeletonLoading';

const FaqsPage = () => {
	const { groupPageId } = useParams();
	const { transformedString, mappedValue: specialty } =
		mapAndTransformSpecialtyString(groupPageId);

	const {
		data: {
			status = undefined,
			data: { resultCode = undefined, data: { entries = [] } = {} } = {},
		} = {},
		isLoading,
		isError,
	} = useQuery(
		['clinicalFAQBySpecialty'],
		() => GetFAQBySpecialtyFn(specialty),
		{
			staleTime: Infinity,
		}
	);

	const showNoResultsOverlay = isError || (status >= 400 && status <= 505);

	const crumbs = [
		{
			text: 'Resources - Providers',
			link: `/${TOP_LEVEL_ROUTES.resources}/providers`,
		},
		{ text: transformedString, link: `/resources/${groupPageId}` },
		{ text: 'FAQ', link: '' },
	];

	return (
		<Container>
			<PageBreadcrumbs crumbs={crumbs} />
			<Typography variant='h4' gutterBottom>
				FAQ
			</Typography>
			<Divider sx={{ my: { xs: 2, md: 3 } }} />
			{isLoading && <FaqsPageSkeletonLoading />}
			{showNoResultsOverlay && (
				<NoResultsOverlay
					resultCode={resultCode}
					statusCode={status}
					customNoResultsVerbiage='No FAQs available.'
					customFailureVerbiage="We're sorry, something went wrong. We're unable to load the FAQs at this time. Please refresh and try again."
				/>
			)}
			{!isLoading && !showNoResultsOverlay && entries.length > 0 && (
				<Grid container mb={{ xs: 4 }}>
					{entries.map(faq => (
						<Grid item xs={12} key={faq.id}>
							<Typography
								variant='body1'
								sx={{ fontWeight: 'bold' }}
								mt={{ xs: 3, md: 0 }}
							>
								{faq.question}
							</Typography>
							<ReactMarkdown
								components={{
									a: MarkdownLinkRenderer,
									p: MarkdownParagraphRenderer,
									ul: MarkdownUnorderListRenderer,
									li: MarkdownListItemRenderer,
								}}
							>
								{faq.answer}
							</ReactMarkdown>
						</Grid>
					))}
				</Grid>
			)}
		</Container>
	);
};

export default FaqsPage;
